import React, {useContext, useEffect, useState} from "react";
import {Button, Card, Divider, Form, Input, message, Select} from "antd";
import {CaretDownOutlined, CloseOutlined} from "@ant-design/icons";
import "../assets/css/PaymentDetails.css"
import {PaymentDetailsResponse as PaymentDetail, PlanResponse} from "../api-client";
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import {getOrganizationApi, getStripeApi} from "../api/apiInstances";
import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";
import {Navigate} from "react-router-dom";
import {FormattedMessage, useIntl} from "react-intl";
import {allCountries} from "../i18n/countries";
import {SubscriptionState} from "../constants/subscriptionStates";
import logger from "../utils/logger";

export interface PaymentDetailsProps {
    createSubscriptionCallback: Function;
    selectedPlanId: string;
    plans: PlanResponse[];
}

interface PaymentFormValues extends PaymentDetail {
    organizationName: string;
    organizationAddress: string;
    town: string;
    organizationCountry: string;
    organizationPostalCode: string;
    email: string;
    vatNumber: string;
}

const PaymentDetails = ({createSubscriptionCallback, selectedPlanId, plans}: PaymentDetailsProps) => {
    const [messageApi, contextHolder] = message.useMessage();
    const stripe = useStripe();
    const elements = useElements();
    const [form] = Form.useForm();
    const stripeApi = getStripeApi();
    const organizationApi = getOrganizationApi();
    const [loading, setLoading] = useState<boolean>(false);
    const {setSelectSubscription, principal, userOrganization} = useContext(DentobookApplicationContext);
    const [redirectToHome, setRedirectToHome] = useState<boolean>(false);

    const [submitted, setSubmitted] = useState(false);
    const intl = useIntl();

    const [cardNumberComplete, setCardNumberComplete] = useState(false);
    const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
    const [cardCvcComplete, setCardCvcComplete] = useState(false);

    function isValidBGRVAT(vatNumber: string): boolean {
        if (!vatNumber) return false;
        const trimmed = vatNumber.trim();

        if (trimmed.toUpperCase().startsWith("BG")) {
            const numericPart = trimmed.substring(2).trim();
            return /^[0-9]{9,10}$/.test(numericPart);
        } else {
            return /^[0-9]{9}$/.test(trimmed);
        }
    }


    const handleClose = () => {
        setRedirectToHome(true);
    };

    if (redirectToHome) {
        setSelectSubscription(SubscriptionState.HIDDEN);
        return <Navigate to="/"/>;
    }

    const onFinish = async (values: PaymentFormValues) => {
        if (!stripe || !elements) {
            messageApi.error(
                <FormattedMessage id="stripe_not_initialized"
                                  defaultMessage="Stripe is not initialized. Please try again."/>
            );
            return;
        }

        const cardNumberElement = elements.getElement(CardNumberElement);
        const cardExpiryElement = elements.getElement(CardExpiryElement);
        const cardCvcElement = elements.getElement(CardCvcElement);

        if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
            messageApi.error(
                <FormattedMessage id="stripe_element_not_loaded"
                                  defaultMessage="Stripe elements are not loaded properly."/>
            );
            return;
        }

        if (
            !values.organizationName ||
            !values.organizationAddress ||
            !values.town ||
            !values.organizationCountry ||
            !values.organizationPostalCode ||
            !values.email ||
            !values.vatNumber ||
            !values.cardholderName ||
            !values.paymentMethod ||
            !values.billingAddress ||
            !values.postalCode ||
            !values.country ||
            !cardNumberComplete ||
            !cardExpiryComplete ||
            !cardCvcComplete
        ) {
            messageApi.error(
                <FormattedMessage id="missing_required_fields" defaultMessage="Please fill out the required fields."/>
            );
            return;
        }

        try {
            const updatedOrganization = {
                ...userOrganization,
                companyName: values.organizationName,
                companyAddress: values.organizationAddress,
                address: values.organizationAddress,
                town: values.town,
                country: values.organizationCountry,
                postalCode: values.organizationPostalCode,
                email: values.email,
                vat: values.vatNumber,
            };

            await organizationApi.updateOrganization(userOrganization?.id as string, updatedOrganization);

            const {paymentMethod, error} = await stripe.createPaymentMethod({
                type: "card",
                card: cardNumberElement,
            });

            if (error) {
                setLoading(false);
                messageApi.error(
                    <FormattedMessage
                        id="payment_method_creation_error"
                        defaultMessage="An error occurred while creating the payment method."
                    />
                );
                return;
            }

            const proccessTokenRes = await stripeApi.processPaymentToken({
                organizationId: principal?.user?.organizationId!,
                paymentToken: paymentMethod.id,
                customerEmail: values.email,
                customerName: values.cardholderName || null,
                billingAddress: {
                    line1: values.organizationAddress,
                    city: values.town,
                    country: values.organizationCountry,
                    postal_code: values.organizationPostalCode
                },
                taxId: values.vatNumber || null
            });

            if (!proccessTokenRes.data.success) {
                setLoading(false);
                const errMsg = <FormattedMessage id={proccessTokenRes.data.paymentId ?? "payment_token_error"}/>;
                messageApi.error(errMsg);
                return;
            }

            if (proccessTokenRes.data.success && proccessTokenRes.data.paymentId) {
                const {error} = await stripe.confirmCardPayment(proccessTokenRes.data.paymentId!, {payment_method: paymentMethod?.id});
                if (error) {
                    messageApi.error(<FormattedMessage
                        id="payment_error"
                    />);

                    return;
                }
            }

            await createSubscriptionCallback();

            setLoading(false);
            messageApi.success(
                <FormattedMessage
                    id="registration_successful"
                    defaultMessage="Registration completed successfully."
                />
            );
        } catch (error) {
            logger.error(error);
            setLoading(false);
            messageApi.error(
                <FormattedMessage
                    id="payment_token_error"
                    defaultMessage="An error occurred during payment. Please check your payment method."
                />
            );
        }
    };

    return (
        <>
            {contextHolder}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                }}
            >
                <div style={{marginBottom: "20px", textAlign: "center"}}>
                    <img
                        alt="logo"
                        src={require("../assets/logo.png")}
                        style={{
                            width: "420px",
                            height: "104px",
                            padding: "13px 10px 13.65px 10px",
                            cursor: "pointer",
                        }}
                        onClick={handleClose}
                    />
                </div>
                <Button
                    className="close-btn"
                    type="text"
                    icon={<CloseOutlined/>}
                    onClick={handleClose}
                    style={{position: 'absolute', top: 10, right: 10}}
                />
                <Card
                    title={<FormattedMessage id="payment_method" defaultMessage="Метод на плащане"/>}
                    style={{
                        width: 480,
                        padding: "40px",
                        borderRadius: "30px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)"
                    }}
                >
                    <Form form={form} layout="vertical" onFinish={onFinish} validateTrigger="onSubmit"
                          style={{textAlign: "left"}}>
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="payment_method" defaultMessage="Метод на плащане"/>}
                            name="paymentMethod"
                            rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                        >
                            <Select
                                style={{borderRadius: "12px", height: "48px"}}
                                className="dentobook-input"
                                allowClear
                                suffixIcon={<CaretDownOutlined/>}
                            >
                                <Select.Option value="creditCard">
                                    <FormattedMessage id="credit_card_title" defaultMessage="С кредитна/дебитна карта"/>
                                </Select.Option>
                            </Select>
                        </Form.Item>
                        <Divider/>
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="name"/>}
                            name="cardholderName"
                            rules={[{
                                required: true,
                                message: <FormattedMessage id="required_field" defaultMessage="The field is required."/>
                            }]}
                        >
                            <Input style={{borderRadius: "12px", height: "48px"}}/>
                        </Form.Item>

                        <Form.Item
                            label={<span>{<FormattedMessage id="card_number_title"/>} <span
                                style={{color: "black"}}>*</span></span>}
                            required={false}
                            validateStatus={submitted && !cardNumberComplete ? "error" : ""}
                            help={submitted && !cardNumberComplete ? intl.formatMessage({
                                id: "required_field",
                                defaultMessage: "The field is required."
                            }) : ""}
                        >
                            <CardNumberElement
                                options={{placeholder: ""}}
                                className={"dentobook-form-item dentobook-simple-input stripe-custom-input"
                                    + (submitted && !cardNumberComplete ? ' stripe-input-error' : '')}
                                onChange={(event) => setCardNumberComplete(event.complete || (!event.complete && !!event.error))}
                            />
                        </Form.Item>

                        <div style={{display: "flex", gap: "16px"}}>
                            <Form.Item
                                label={<span>{<FormattedMessage id="card_expiration_title"/>} <span
                                    style={{color: "black"}}>*</span></span>}
                                required={false}
                                validateStatus={submitted && !cardExpiryComplete ? "error" : ""}
                                help={submitted && !cardExpiryComplete ? intl.formatMessage({id: "required_field"}) : ""}
                                style={{width: "100%"}}
                            >
                                <CardExpiryElement
                                    options={{placeholder: ""}}
                                    className={"dentobook-form-item dentobook-simple-input stripe-custom-input"
                                        + (submitted && !cardExpiryComplete ? ' stripe-input-error' : '')}
                                    onChange={(event) => setCardExpiryComplete(event.complete || (!event.complete && !!event.error))}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span>CVV <span style={{color: "black"}}>*</span></span>}
                                required={false}
                                validateStatus={submitted && !cardCvcComplete ? "error" : ""}
                                help={submitted && !cardCvcComplete ? intl.formatMessage({id: "required_field"}) : ""}
                                style={{width: "100%"}}
                            >
                                <CardCvcElement
                                    options={{placeholder: ""}}
                                    className={"dentobook-form-item dentobook-simple-input stripe-custom-input"
                                        + (submitted && !cardCvcComplete ? ' stripe-input-error' : '')}
                                    onChange={(event) => setCardCvcComplete(event.complete || (!event.complete && !!event.error))}
                                />
                            </Form.Item>
                        </div>


                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="address"/>}
                            name="billingAddress"
                            rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                        >
                            <Input style={{borderRadius: "12px", height: "48px"}}/>
                        </Form.Item>

                        <div style={{display: "flex", gap: "16px"}}>
                            <Form.Item
                                className={"dentobook-form-item dentobook-simple-input single-select"}
                                label={<FormattedMessage id="country"/>}
                                name="country"
                                style={{flex: 1}}
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                            >
                                <Select
                                    suffixIcon={<CaretDownOutlined/>}
                                    className="dentobook-input"
                                >
                                    {allCountries.map(countryItem => (
                                        <Select.Option key={countryItem.code}
                                                       value={countryItem.code}>{intl.locale === "BG" ? countryItem.name_bg : countryItem.name_en}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                className={"dentobook-form-item dentobook-simple-input"}
                                label={<FormattedMessage id="postal_code_title"/>}
                                name="postalCode"
                                style={{flex: 1}}
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                            >
                                <Input style={{borderRadius: "12px", height: "48px"}}/>
                            </Form.Item>
                        </div>

                        <div className="ant-card-head" style={{ marginTop: '20px', marginBottom: '30px' }}>
                            <div className="ant-card-head-wrapper">
                                <div className="ant-card-head-title"><FormattedMessage id="billing_details"/>
                                </div>
                            </div>
                        </div>
                        <Form.Item
                            initialValue={userOrganization?.companyName}
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="company_name"/>}
                            name="organizationName"
                            rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                        >
                            <Input style={{borderRadius: "12px", height: "48px"}}/>
                        </Form.Item>

                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="address"/>}
                            name="organizationAddress"
                            rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                        >
                            <Input style={{borderRadius: "12px", height: "48px"}}/>
                        </Form.Item>
                        <Form.Item
                            initialValue={userOrganization?.town}
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="town"/>}
                            name="town"
                            rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                        >
                            <Input style={{borderRadius: "12px", height: "48px"}}/>
                        </Form.Item>
                        <div style={{display: "flex", gap: "16px"}}>
                            <Form.Item
                                className={"dentobook-form-item dentobook-simple-input single-select"}
                                label={<FormattedMessage id="country"/>}
                                name="organizationCountry"
                                style={{flex: 1}}
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                                initialValue={userOrganization?.country}
                            >
                                <Select
                                    suffixIcon={<CaretDownOutlined/>}
                                    className="dentobook-input"
                                >
                                    {allCountries.map(countryItem => (
                                        <Select.Option key={countryItem.code}
                                                       value={countryItem.code}>{intl.locale === "BG" ? countryItem.name_bg : countryItem.name_en}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                initialValue={userOrganization?.postalCode}
                                className={"dentobook-form-item dentobook-simple-input"}
                                label={<FormattedMessage id="postal_code_title"/>}
                                name="organizationPostalCode"
                                style={{flex: 1}}
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                            >
                                <Input style={{borderRadius: "12px", height: "48px"}}/>
                            </Form.Item>
                        </div>
                        <div style={{display: "flex", gap: "16px"}}>
                            <Form.Item
                                initialValue={userOrganization?.email}
                                className={"dentobook-form-item dentobook-simple-input"}
                                label={<FormattedMessage id="email"/>}
                                name="email"
                                style={{flex: 1}}
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                            >
                                <Input style={{borderRadius: "12px", height: "48px"}}/>
                            </Form.Item>
                            <Form.Item
                                initialValue={userOrganization?.vat}
                                className={"dentobook-form-item dentobook-simple-input"}
                                label={<FormattedMessage id="vat"/>}
                                name="vatNumber"
                                style={{ flex: 1 }}
                                rules={[
                                    { required: true, message: intl.formatMessage({ id: 'required_field' }) },
                                    () => ({
                                        validator(_, value) {

                                            const orgCountry = userOrganization?.country?.toUpperCase();

                                            // Apply BG VAT format validation only if the organization country is BG
                                            if (orgCountry === 'BG') {
                                                // Let the required rule handle empty values
                                                if (!value) {
                                                    return Promise.resolve();
                                                }
                                                if (isValidBGRVAT(value)) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error(intl.formatMessage({ id: 'invalid_vat_bg' })));
                                            }
                                            // For all other countries, skip additional validation
                                            return Promise.resolve();
                                        }
                                    })
                                ]}
                            >
                                <Input style={{ borderRadius: "12px", height: "48px" }} />
                            </Form.Item>

                        </div>


                        <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
                            <Button
                                className="dentobook-btn-primary dentobook-btn-medium"
                                onClick={() => setSubmitted(true)}
                                type="primary"
                                disabled={loading}
                                htmlType="submit"
                            >
                                <FormattedMessage id="confirm"/>
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </>
    );
};

export default PaymentDetails;
